/**
 * Archive constants — the fixed vocabulary the archive is built on.
 *
 * Two kinds of thing live here, and both are here for the same reason: they are
 * DATA, so they can be checked without rendering a component or calling a
 * function. The filter names, sort keys and option values are pinned by
 * client/src/lib/archive.test.mjs and test/archive.filters.test.js; the help
 * text's wording is pinned by client/src/constants/archive.test.mjs.
 *
 * Several of these are one half of a contract with the server. Where that is
 * true the comment says which file holds the other half — change one without
 * the other and the archive fails silently rather than loudly, which has
 * happened before (see ARCHIVED_OPTIONS).
 *
 * The behaviour that READS these — parsing the URL, building the query string,
 * debouncing the boxes — stays in lib/archive.mjs.
 */

/*
 * The FALLBACK page size, for a response that did not say. The server decides
 * how big a page is — parseFilters in routes/archive.js pins pageSize and
 * searchCampaigns caps it — and it echoes that back in `filters.pageSize`, which
 * is what the view passes to archivePages.
 *
 * This was 50 while the server served 25, and the two never met: the pager sized
 * itself at 50, so it offered 8 pages of a 400-campaign archive, stopped at
 * "Page 8 of 8", and left 200 campaigns with no way to reach them at all. It
 * announced "Showing 351–400 of 400" over rows 176–200, so the archive looked
 * complete while hiding half of itself. Keep this in step with the server, and
 * prefer the served value over it.
 */
export const ARCH_PAGE_SIZE = 25;

/*
 * The sortable columns, in the order the original showed them. First Seen and
 * Days Active were dropped in the rebuild; without them a row gives no sense of
 * how long a campaign actually ran, which is most of what the archive is for.
 *
 * The `value`s are SORT_COLUMNS keys in db/campaignArchive.js — snake_case for
 * the two date columns, which is not a style choice: anything the server does
 * not recognise falls back to total_spend, so `firstSeen` here meant clicking
 * "First Seen" moved the arrow and left the rows in spend order.
 */
export const ARCHIVE_SORTS = [
  { value: 'first_seen', label: 'First Seen' },
  { value: 'last_seen', label: 'Last Seen' },
  { value: 'days', label: 'Days Active' },
  { value: 'spend', label: 'Total Spend' },
  { value: 'revenue', label: 'Total Revenue' },
  { value: 'roas', label: 'ROAS' },
  { value: 'purchases', label: 'Purchases' },
  // Last, next to the purchases it divides by. This array's ORDER is the
  // table's column order — the header row is generated from it and the body
  // writes its cells in the same sequence, so moving an entry here moves a
  // column and nothing else.
  { value: 'cpa', label: 'CPA' },
];

/**
 * The Account Status filter — the chip row and the Account Status select are
 * two faces of this one list, so they can never offer different things.
 *
 * These values reach the server VERBATIM: `archived` is a raw passthrough key
 * in FILTER_KEYS below, so whatever is written here is what buildFilters in
 * server/db/campaignArchive.js compares against. parseFilters in
 * routes/archive.js documents the contract as 'active' | 'archived' | null, and
 * '' means "all" by being omitted from the query entirely (see archiveQuery in lib/archive.mjs).
 *
 * REGRESSION GUARD: these were once 'false'/'true', on the reasonable-looking
 * assumption that a filter named `archived` takes a boolean. Nothing errored.
 * The server matched neither branch, added no HAVING clause, and returned the
 * whole archive with the chip still lit and the tiles recalculated to the same
 * figures — so it read as "there is nothing to filter out" rather than "this is
 * broken", and went unnoticed. test/archive.filters.test.js now pins these
 * against the server's own branches, so a rename on either side fails the run.
 */
export const ARCHIVED_OPTIONS = [
  { value: '', label: 'All', chip: 'ALL' },
  { value: 'active', label: 'Active accounts only', chip: 'ACTIVE ONLY' },
  { value: 'archived', label: 'Archived / banned only', chip: 'ARCHIVED / BANNED' },
];

/**
 * Every filter the archive understands, and the ONLY list of them.
 *
 * These are query-string keys sent to /api/archive/search verbatim, so each one
 * is also a name parseFilters in routes/archive.js has to know. Adding a filter
 * means adding it here, to emptyArchiveFilters in lib/archive.mjs, and to the
 * server — in that order, because this list is what parseArchiveFilters,
 * archiveQuery and hasActiveFilters all iterate.
 */
export const FILTER_KEYS = ['q', 'account', 'archived', 'minSpend', 'maxSpend', 'minRoas', 'maxRoas', 'minCpa', 'maxCpa', 'dateFrom', 'dateTo'];

/*
 * The filters a user TYPES INTO, rather than picks — so the ones that must not
 * fire a search per keystroke.
 *
 * A number box is worse than the search box here, not better. Typing "1500"
 * into Max Spend passes through 1, 15 and 150 on the way, and each of those is
 * a perfectly valid filter: the page runs four searches, three of them for
 * bounds nobody asked for, and the rows flicker through three wrong answers
 * before landing on the right one. Every one of those searches is four database
 * queries (rows, accounts, totals, all-time totals).
 *
 * Selects and date pickers are deliberately NOT here. One click is one intent,
 * so waiting half a second after it would only feel slow.
 */
export const DEBOUNCED_FILTER_KEYS = ['q', 'minSpend', 'maxSpend', 'minRoas', 'maxRoas', 'minCpa', 'maxCpa'];

/*
 * Long enough that an ordinary typing rhythm never breaks it, short enough that
 * a pause reads as "it's fetching" rather than "it's stuck". Shared by all seven
 * boxes so they cannot drift apart.
 */
export const FILTER_DEBOUNCE_MS = 500;

/**
 * The archive's "Quick guide" text.
 *
 * Kept as data, separate from the component, so its WORDING can be tested
 * without rendering anything — which is what test/archive.help.test.js did
 * against the original, and what client/src/constants/archive.test.mjs
 * does now.
 *
 * This panel is read by the client, not by a developer. It exists because
 * "ARCHIVED" means two different things on this screen — a campaign status and
 * an ad-account state — and that genuinely confuses people. Keep it plain: no
 * jargon, no sentence over 34 words. Both rules are enforced by the test.
 */
export const ARCHIVE_HELP = [
  {
    title: 'Campaigns or Ads',
    body: 'Pick which list you want. Campaigns shows one row per campaign. Ads shows one row per ad. All the filters work the same in both. In the Ads list, typing a name finds the ad or the campaign it belongs to.',
  },
  {
    title: 'ACTIVE, PAUSED or DELETED',
    body: 'This is about the campaign. DELETED means it has been removed from Facebook, so you will not find it in Ads Manager any more. Its results are saved here, and nowhere else.',
  },
  {
    title: 'The red ARCHIVED tag under an account name',
    body: 'This one is about the ad account, not the campaign. It means the account was switched off, retired or banned. So an ACTIVE campaign inside an ARCHIVED account is normal — the campaign was still running when the account was closed. The "Archived / banned only" option looks at this tag.',
  },
  {
    title: 'Ran From and Ran To',
    body: 'Pick two dates to see how things did in that time only. Spend, revenue and ROAS will then cover just those days. Leave both empty to see everything, all the way back.',
  },
  {
    title: 'Min and Max Spend / ROAS / CPA',
    body: 'Shows only the campaigns above or below a number. These look at the total, not one single day. If you have picked dates, they use the total for those dates.',
  },
  {
    title: 'CPA, and why some rows show a dash',
    body: 'CPA is the spend shared out over the purchases — what each sale cost to buy. Something that has not sold yet has no CPA at all, so it shows a dash. It is left out of a Min or Max CPA search, and it never counts as the cheapest.',
  },
  {
    title: 'The boxes at the top',
    body: 'They always add up exactly what is in the list below. Use any filter and they change to match, with the full total shown underneath so you can compare.',
  },
  {
    title: 'Cannot find something?',
    body: 'New results are saved every night. Anything older can be brought in from Facebook using Import History below. If Facebook has banned an account, its results cannot be brought in at all — you will see it listed so you know what is missing.',
  },
];
